import { Shipment, TelemetryReading, Alert, Vehicle } from './api';

declare const jspdf: any;

interface ViolationRecord {
  timestamp: string;
  temperature: number;
  threshold: string;
  deviation: string;
}

export function generateComplianceReport(
  shipment: Shipment,
  telemetry: TelemetryReading[],
  alerts: Alert[],
  vehicle: Vehicle | null,
) {
  const { jsPDF } = jspdf;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const reportId = `CR-${shipment.id}-${Date.now().toString(36).toUpperCase()}`;
  const generatedAt = new Date().toLocaleString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
  });

  function checkPage(needed: number) {
    if (y + needed > pageHeight - 20) {
      doc.addPage();
      y = margin;
    }
  }

  function sectionTitle(title: string) {
    checkPage(14);
    y += 4;
    doc.setFillColor(30, 41, 59);
    doc.rect(margin, y, contentWidth, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(148, 163, 184);
    doc.text(title.toUpperCase(), margin + 3, y + 5.5);
    y += 12;
  }

  function labelValue(label: string, value: string, x: number, w: number) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(label, x, y);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(30, 41, 59);
    doc.text(value, x, y + 4.5);
  }

  // --- Header ---
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageWidth, 38, 'F');

  doc.setFillColor(37, 99, 235);
  doc.roundedRect(margin, 8, 10, 10, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.text('FT', margin + 2.4, 15);

  doc.setFontSize(16);
  doc.text('Cold Chain Compliance Report', margin + 14, 14);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text(`Report ID: ${reportId}`, margin + 14, 20);
  doc.text(`Generated: ${generatedAt}`, margin + 14, 25);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(255, 255, 255);
  doc.text(shipment.id, pageWidth - margin, 16, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(96, 165, 250);
  const statusLabel = shipment.status.replace('_', ' ').toUpperCase();
  doc.text(statusLabel, pageWidth - margin, 23, { align: 'right' });

  y = 46;

  // --- Shipment Overview ---
  sectionTitle('Shipment Overview');

  const col1 = margin + 3;
  const col2 = margin + contentWidth * 0.35;
  const col3 = margin + contentWidth * 0.65;

  labelValue('Origin', shipment.origin, col1, contentWidth * 0.3);
  labelValue('Destination', shipment.destination, col2, contentWidth * 0.3);
  labelValue('Cargo Type', shipment.cargo_type, col3, contentWidth * 0.3);
  y += 10;

  labelValue('Cargo Description', shipment.cargo_description || '—', col1, contentWidth * 0.3);
  labelValue('Vehicle', vehicle ? `${vehicle.name} (${vehicle.id})` : shipment.vehicle_id, col2, contentWidth * 0.3);
  labelValue('Driver', vehicle?.current_driver || '—', col3, contentWidth * 0.3);
  y += 10;

  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';

  labelValue('Pickup Time', fmtDate(shipment.pickup_time), col1, contentWidth * 0.3);
  labelValue('ETA', fmtDate(shipment.eta), col2, contentWidth * 0.3);
  labelValue('Actual Delivery', fmtDate(shipment.actual_delivery), col3, contentWidth * 0.3);
  y += 14;

  // --- Temperature Compliance ---
  sectionTitle('Temperature Compliance');

  const sorted = [...telemetry].sort((a, b) => a.temperature - b.temperature);
  const temps = telemetry.map(t => t.temperature);
  const totalReadings = temps.length;

  let observedMin = 0, observedMax = 0, observedAvg = 0;
  let inRangeCount = 0;
  const violations: ViolationRecord[] = [];

  if (totalReadings > 0) {
    observedMin = Math.min(...temps);
    observedMax = Math.max(...temps);
    observedAvg = temps.reduce((sum, t) => sum + t, 0) / totalReadings;

    telemetry.forEach(t => {
      if (t.temperature >= shipment.temp_min && t.temperature <= shipment.temp_max) {
        inRangeCount++;
      } else {
        const isOver = t.temperature > shipment.temp_max;
        violations.push({
          timestamp: new Date(t.timestamp).toLocaleString('en-GB', {
            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
          }),
          temperature: t.temperature,
          threshold: isOver ? `Max ${shipment.temp_max}°C` : `Min ${shipment.temp_min}°C`,
          deviation: isOver
            ? `+${(t.temperature - shipment.temp_max).toFixed(1)}°C`
            : `${(t.temperature - shipment.temp_min).toFixed(1)}°C`,
        });
      }
    });
  }

  const compliancePct = totalReadings > 0 ? ((inRangeCount / totalReadings) * 100) : 0;
  const isCompliant = compliancePct === 100;

  // Compliance badge
  checkPage(30);
  const badgeW = 50;
  const badgeX = margin + contentWidth - badgeW;
  if (isCompliant) {
    doc.setFillColor(220, 252, 231);
    doc.roundedRect(badgeX, y - 4, badgeW, 14, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(22, 163, 74);
    doc.text('COMPLIANT', badgeX + badgeW / 2, y + 4, { align: 'center' });
  } else {
    doc.setFillColor(254, 226, 226);
    doc.roundedRect(badgeX, y - 4, badgeW, 14, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(220, 38, 38);
    doc.text('NON-COMPLIANT', badgeX + badgeW / 2, y + 4, { align: 'center' });
  }

  labelValue('Required Range', `${shipment.temp_min}°C to ${shipment.temp_max}°C`, col1, contentWidth * 0.3);
  labelValue('Compliance', `${compliancePct.toFixed(1)}%`, col2, contentWidth * 0.15);
  y += 10;

  labelValue('Observed Min', totalReadings > 0 ? `${observedMin.toFixed(1)}°C` : '—', col1, contentWidth * 0.2);
  labelValue('Observed Max', totalReadings > 0 ? `${observedMax.toFixed(1)}°C` : '—', col2, contentWidth * 0.2);
  labelValue('Observed Avg', totalReadings > 0 ? `${observedAvg.toFixed(1)}°C` : '—', col3, contentWidth * 0.2);
  y += 10;

  labelValue('Total Readings', `${totalReadings}`, col1, contentWidth * 0.2);
  labelValue('In Range', `${inRangeCount}`, col2, contentWidth * 0.2);
  labelValue('Out of Range', `${violations.length}`, col3, contentWidth * 0.2);
  y += 12;

  // --- Threshold Violations ---
  if (violations.length > 0) {
    sectionTitle(`Threshold Violations (${violations.length})`);

    const tableViolations = violations.slice(0, 50);

    (doc as any).autoTable({
      startY: y,
      margin: { left: margin, right: margin },
      head: [['#', 'Timestamp', 'Temperature', 'Threshold', 'Deviation']],
      body: tableViolations.map((v, i) => [
        i + 1,
        v.timestamp,
        `${v.temperature.toFixed(1)}°C`,
        v.threshold,
        v.deviation,
      ]),
      styles: {
        fontSize: 8,
        cellPadding: 2,
        textColor: [30, 41, 59],
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: [30, 41, 59],
        textColor: [226, 232, 240],
        fontStyle: 'bold',
        fontSize: 8,
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 40 },
        2: { cellWidth: 28, halign: 'center' },
        3: { cellWidth: 32, halign: 'center' },
        4: { cellWidth: 28, halign: 'center' },
      },
    });

    y = (doc as any).lastAutoTable.finalY + 4;

    if (violations.length > 50) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text(`Showing first 50 of ${violations.length} violations`, margin + 3, y);
      y += 6;
    }
  }

  // --- Chain of Custody / Events Timeline ---
  const shipmentAlerts = alerts.filter(a => a.shipment_id === shipment.id);

  sectionTitle('Chain of Custody — Event Timeline');

  if (shipmentAlerts.length === 0 && totalReadings > 0) {
    checkPage(10);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text('No events or alerts recorded for this shipment.', margin + 3, y);
    y += 8;
  } else if (shipmentAlerts.length > 0) {
    const eventLabels: Record<string, string> = {
      temp_excursion: 'Temperature Excursion',
      door_open: 'Door Opened',
      gps_lost: 'GPS Signal Lost',
      route_deviation: 'Route Deviation',
      delivery_arrival: 'Delivery Arrival',
    };

    const sortedAlerts = [...shipmentAlerts].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    (doc as any).autoTable({
      startY: y,
      margin: { left: margin, right: margin },
      head: [['#', 'Time', 'Event', 'Severity', 'Description', 'Status']],
      body: sortedAlerts.map((a, i) => [
        i + 1,
        new Date(a.created_at).toLocaleString('en-GB', {
          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
        }),
        eventLabels[a.event_type] || a.event_type,
        a.severity.toUpperCase(),
        a.description,
        a.resolved
          ? `Resolved ${a.resolved_at ? new Date(a.resolved_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}`
          : 'OPEN',
      ]),
      styles: {
        fontSize: 7.5,
        cellPadding: 2,
        textColor: [30, 41, 59],
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [30, 41, 59],
        textColor: [226, 232, 240],
        fontStyle: 'bold',
        fontSize: 8,
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      columnStyles: {
        0: { cellWidth: 8, halign: 'center' },
        1: { cellWidth: 28 },
        2: { cellWidth: 32 },
        3: { cellWidth: 18, halign: 'center' },
        4: { cellWidth: 'auto' },
        5: { cellWidth: 30 },
      },
    });

    y = (doc as any).lastAutoTable.finalY + 4;
  }

  // --- Humidity Compliance (if monitored) ---
  if (shipment.humidity_min != null && shipment.humidity_max != null && totalReadings > 0) {
    sectionTitle('Humidity Compliance');

    const humidities = telemetry.map(t => t.humidity);
    const humMin = Math.min(...humidities);
    const humMax = Math.max(...humidities);
    const humAvg = humidities.reduce((s, h) => s + h, 0) / humidities.length;
    const humInRange = humidities.filter(
      h => h >= (shipment.humidity_min as number) && h <= (shipment.humidity_max as number)
    ).length;
    const humPct = (humInRange / humidities.length) * 100;

    labelValue('Required Range', `${shipment.humidity_min}% to ${shipment.humidity_max}%`, col1, contentWidth * 0.3);
    labelValue('Compliance', `${humPct.toFixed(1)}%`, col2, contentWidth * 0.15);
    y += 10;

    labelValue('Observed Min', `${humMin.toFixed(0)}%`, col1, contentWidth * 0.2);
    labelValue('Observed Max', `${humMax.toFixed(0)}%`, col2, contentWidth * 0.2);
    labelValue('Observed Avg', `${humAvg.toFixed(0)}%`, col3, contentWidth * 0.2);
    y += 12;
  }

  // --- Certification Footer ---
  checkPage(40);
  y += 4;
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.3);
  doc.line(margin, y, margin + contentWidth, y);
  y += 8;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59);
  doc.text('Certification', margin + 3, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(71, 85, 105);
  const certText = [
    'This report was generated automatically by the FrostTrack Cold Chain Monitoring System.',
    `All temperature and humidity readings are captured by on-board sensors at 5-minute intervals.`,
    `Data integrity is maintained through continuous telemetry logging with tamper-evident timestamps.`,
  ];
  certText.forEach(line => {
    doc.text(line, margin + 3, y);
    y += 4;
  });

  y += 6;
  doc.setDrawColor(148, 163, 184);
  doc.setLineDashPattern([1, 1], 0);
  doc.line(margin + 3, y, margin + 60, y);
  doc.line(margin + contentWidth - 60, y, margin + contentWidth - 3, y);
  y += 4;
  doc.setFontSize(7);
  doc.setTextColor(148, 163, 184);
  doc.text('Authorized Signature', margin + 3, y);
  doc.text('Date', margin + contentWidth - 60, y);

  // Page numbers
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(`FrostTrack — ${reportId}`, margin, pageHeight - 8);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
  }

  doc.save(`${shipment.id}-compliance-report.pdf`);
}
